import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, orderBy, getDoc, doc, onSnapshot, addDoc, updateDoc, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Search,
  Plus,
  X,
  DollarSign,
  Calendar,
  GripVertical,
  LayoutGrid,
  ArrowRight,
  Gavel,
  PackageOpen,
  UserPlus,
  Flag,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../../contexts/ResponsiveContext';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';
import BackButton from '../../components/BackButton';
import HiringAvatarCluster from '../../components/hr/HiringAvatarCluster';
import { fetchPortalUsersLinkedToAccounts } from '../../services/accountTeamMembersService';
import { computeHiringBalance } from '../../utils/hiringBalance';

// Kanban columns, in pipeline order. "Available" is a derived, internal-only
// stage (clients with an unused/available Hiring deliverable); the rest map to a
// position request's stored status. "Bid" is a CRM-only status that never
// surfaces on the client portal's hiring hub. There is no "Pending" stage — a
// request goes straight to In Progress, then Completed.
const KANBAN_COLUMNS = [
  {
    key: 'bid',
    title: 'Bid',
    emoji: '💲',
    color: '#EC4899',
    tile: 'bg-pink-50 dark:bg-pink-900/30',
    text: 'text-pink-600 dark:text-pink-400',
    ring: 'ring-pink-500',
    empty: 'No bids yet — drag a request here to mark it as a bid.',
    Icon: Gavel,
  },
  {
    key: 'in-package',
    title: 'Available',
    emoji: '🎯',
    color: '#6366F1',
    tile: 'bg-indigo-50 dark:bg-indigo-900/30',
    text: 'text-indigo-600 dark:text-indigo-400',
    ring: 'ring-indigo-500',
    empty: 'No clients have an unused hire in their package.',
    Icon: PackageOpen,
  },
  {
    key: 'in-progress',
    title: 'In Progress',
    emoji: '🚀',
    color: '#3B82F6',
    tile: 'bg-blue-50 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    ring: 'ring-blue-500',
    empty: 'Nothing is being worked right now.',
    Icon: UserPlus,
  },
  {
    key: 'completed',
    title: 'Completed',
    emoji: '🏁',
    color: '#10B981',
    tile: 'bg-emerald-50 dark:bg-emerald-900/30',
    text: 'text-emerald-600 dark:text-emerald-400',
    ring: 'ring-emerald-500',
    empty: 'No completed hires yet.',
    Icon: Flag,
  },
];

// Request-status columns that accept drag-and-drop (Available is derived, not a
// stored status, so it is neither draggable-from nor a drop target).
const REQUEST_COLUMN_KEYS = ['bid', 'in-progress', 'completed'];

// Which stored request statuses fall under each request column. Legacy
// 'pending'/'approved' requests fold into In Progress (the Pending stage was
// removed); cancelled/denied are terminal and stay off the board.
const STATUS_TO_COLUMN = {
  bid: 'bid',
  pending: 'in-progress',
  approved: 'in-progress',
  'in-progress': 'in-progress',
  completed: 'completed',
};

// Column → the status written back on the positionRequest, and the mirrored
// `positions` doc status shown in the client portal. 'Bid' is deliberately NOT
// one of the portal's displayed statuses, so bids stay CRM-only.
const COLUMN_TO_REQUEST_STATUS = {
  bid: 'bid',
  'in-progress': 'in-progress',
  completed: 'completed',
};
const COLUMN_TO_POSITION_STATUS = {
  bid: 'Bid',
  'in-progress': 'In Progress',
  completed: 'Completed',
};

const URGENCY_STYLES = {
  critical: { chip: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', label: 'Critical' },
  high: { chip: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', label: 'High' },
  normal: { chip: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300', label: 'Normal' },
  low: { chip: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', label: 'Low' },
};

const HRFulfillment = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [positionRequests, setPositionRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Client accounts + their Hiring deliverable balances (drives "Available").
  const [clients, setClients] = useState([]);
  const [hiringByAccountId, setHiringByAccountId] = useState({});
  const [teamByAccountId, setTeamByAccountId] = useState({});
  const [loadingPackage, setLoadingPackage] = useState(true);

  // Drag-and-drop status change
  const [draggingRequestId, setDraggingRequestId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Search + stage filter state ('' = full board)
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Manual position-request creation (on behalf of a client)
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    accountId: '',
    position: '',
    requirements: '',
    urgency: 'normal',
    budget: ''
  });

  const clientsLoading = loadingPackage && clients.length === 0;

  // Load client accounts once and compute each one's Hiring deliverable balance.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPackage(true);
      try {
        const snap = await getDocs(query(collection(db, 'accounts'), where('type', '==', 'Clients')));
        if (cancelled) return;
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.company)
          .sort((a, b) => String(a.company).localeCompare(String(b.company)));
        setClients(list);

        // Team members (portal users linked to each account) for the card names.
        fetchPortalUsersLinkedToAccounts(list)
          .then((teamMap) => {
            if (cancelled) return;
            const next = {};
            teamMap.forEach((members, id) => { next[id] = members || []; });
            setTeamByAccountId(next);
          })
          .catch((err) => console.error('Error loading account team members:', err));

        const balances = await Promise.all(list.map((account) => computeHiringBalance(account)));
        if (cancelled) return;
        const map = {};
        list.forEach((account, idx) => {
          map[account.id] = balances[idx];
        });
        setHiringByAccountId(map);
      } catch (error) {
        console.error('Error loading client accounts / hiring balances:', error);
      } finally {
        if (!cancelled) setLoadingPackage(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resetCreateForm = () => {
    setCreateForm({ accountId: '', position: '', requirements: '', urgency: 'normal', budget: '' });
  };

  // Open the create modal pre-scoped to a client (from an "Available" card).
  const openCreateForClient = (account) => {
    setCreateForm({ accountId: account.id, position: '', requirements: '', urgency: 'normal', budget: '' });
    setShowCreateModal(true);
  };

  // Create a position request on behalf of a selected client account.
  // Links to the client's portal user (by contact email) when one exists, so
  // it also surfaces in their portal hiring hub.
  const handleCreateRequest = async () => {
    const account = clients.find((c) => c.id === createForm.accountId);
    if (!account || !createForm.position.trim()) return;

    setCreating(true);
    try {
      const companyName = account.company || 'Unknown Company';
      const contact = account.contact || {};
      const contactEmail = contact.email || '';

      // Match the client's portal user by contact email (best effort)
      let portalUserId = null;
      if (contactEmail) {
        try {
          const usersSnap = await getDocs(
            query(collection(db, 'users'), where('email', '==', contactEmail))
          );
          if (!usersSnap.empty) portalUserId = usersSnap.docs[0].id;
        } catch (error) {
          console.error('Error matching portal user:', error);
        }
      }

      const now = new Date().toISOString();
      const requestData = {
        userId: portalUserId,
        userEmail: contactEmail,
        accountId: account.id,
        company: companyName,
        companyName,
        mainContactName: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || companyName,
        mainContactEmail: contactEmail,
        mainContactPhone: contact.phone || '',
        position: createForm.position.trim(),
        requirements: createForm.requirements.trim(),
        urgency: createForm.urgency,
        budget: createForm.budget.trim(),
        status: 'in-progress',
        createdManually: true,
        createdBy: currentUser?.uid || null,
        createdAt: now,
        updatedAt: now
      };

      const requestRef = await addDoc(collection(db, 'positionRequests'), requestData);

      // When linked to a portal user, mirror a position into their hiring hub
      if (portalUserId) {
        const priority =
          createForm.urgency === 'critical' ? 'Critical' :
          createForm.urgency === 'high' ? 'High' :
          createForm.urgency === 'low' ? 'Low' : 'Medium';

        await addDoc(collection(db, 'positions'), {
          userId: portalUserId,
          title: createForm.position.trim(),
          department: 'TBD',
          location: 'TBD',
          type: 'Full-time',
          status: 'In Progress',
          applications: 0,
          postedDate: now.split('T')[0],
          deadline: null,
          priority,
          description: createForm.requirements.trim() || 'Requirements to be defined',
          budget: createForm.budget.trim(),
          positionRequestId: requestRef.id,
          createdManually: true,
          createdAt: now,
          updatedAt: now
        });
      }

      setShowCreateModal(false);
      resetCreateForm();
    } catch (error) {
      console.error('Error creating position request:', error);
      alert('Error creating position request. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  // Fetch position requests data with real-time updates
  useEffect(() => {
    let unsubscribe = null;

    const fetchPositionRequests = async () => {
      try {
        const requestsRef = collection(db, 'positionRequests');
        const q = query(requestsRef, orderBy('createdAt', 'desc'));

        unsubscribe = onSnapshot(q, async (querySnapshot) => {
          const fetchedRequests = await Promise.all(
            querySnapshot.docs.map(async (docSnapshot) => {
              const data = docSnapshot.data();

              // Get user display name and company if userId exists
              let requestedByName = 'Unknown User';
              let companyName = 'Unknown Company';
              if (data.userId) {
                try {
                  const userDocRef = doc(db, 'users', data.userId);
                  const userDoc = await getDoc(userDocRef);
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    requestedByName = userData.displayName ||
                                    (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                                    userData.email ||
                                    'Unknown User';
                    companyName = userData.company || 'Unknown Company';
                  }
                } catch (error) {
                  console.error('Error fetching user data:', error);
                }
              }

              // Manually-created requests may have no portal user — fall back to
              // the company/contact stored on the request itself.
              if (companyName === 'Unknown Company') {
                companyName = data.company || data.companyName || companyName;
              }
              if (requestedByName === 'Unknown User') {
                requestedByName = data.mainContactName || data.company || requestedByName;
              }

              return {
                id: docSnapshot.id,
                ...data,
                status: data.status || 'in-progress',
                requestDate: data.createdAt || new Date().toISOString(),
                requestedByName,
                companyName,
                urgency: data.urgency || 'normal'
              };
            })
          );

          setPositionRequests(fetchedRequests);
          setLoading(false);
        }, (error) => {
          console.error('Error fetching position requests:', error);
          setPositionRequests([]);
          setLoading(false);
        });
      } catch (error) {
        console.error('Error setting up position requests query:', error);
        setPositionRequests([]);
        setLoading(false);
      }
    };

    fetchPositionRequests();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const urgencyChip = (urgency) => {
    const style = URGENCY_STYLES[urgency] || URGENCY_STYLES.normal;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${style.chip}`}>
        {style.label}
      </span>
    );
  };

  // Resolve the client account for a request (for the card avatar), by explicit
  // link first, then a best-effort company-name match.
  const clientsById = useMemo(() => {
    const map = {};
    clients.forEach((c) => { map[c.id] = c; });
    return map;
  }, [clients]);

  const clientsByCompany = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      if (c.company) map[String(c.company).toLowerCase().trim()] = c;
    });
    return map;
  }, [clients]);

  const resolveRequestAccount = (request) => {
    if (request.accountId && clientsById[request.accountId]) return clientsById[request.accountId];
    const name = (request.companyName || request.company || '').toLowerCase().trim();
    return name ? clientsByCompany[name] || null : null;
  };

  // Full team-member names for an account (table view), else CRM primary contact.
  const teamNamesFor = (account) => {
    const members = account ? (teamByAccountId[account.id] || []) : [];
    if (members.length) return members.map((m) => m.name).filter(Boolean).join(', ');
    return account ? `${account.contact?.firstName || ''} ${account.contact?.lastName || ''}`.trim() : '';
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatShortDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const searchLower = search.toLowerCase().trim();

  // "Available": clients with an unused Hiring deliverable.
  const inPackageClients = clients.filter((account) => {
    const balance = hiringByAccountId[account.id];
    if (!balance || balance.remaining <= 0) return false;
    if (!searchLower) return true;
    return String(account.company || '').toLowerCase().includes(searchLower) ||
      `${account.contact?.firstName || ''} ${account.contact?.lastName || ''}`.toLowerCase().includes(searchLower);
  });

  // Bucket requests into their columns after applying search.
  const searchedRequests = positionRequests.filter((request) => {
    if (!searchLower) return true;
    return request.position?.toLowerCase().includes(searchLower) ||
      request.requestedByName?.toLowerCase().includes(searchLower) ||
      request.companyName?.toLowerCase().includes(searchLower) ||
      request.userEmail?.toLowerCase().includes(searchLower) ||
      request.requirements?.toLowerCase().includes(searchLower) ||
      request.budget?.toLowerCase().includes(searchLower);
  });

  const requestsByColumn = { bid: [], 'in-progress': [], completed: [] };
  searchedRequests.forEach((request) => {
    const column = STATUS_TO_COLUMN[request.status];
    if (column && requestsByColumn[column]) requestsByColumn[column].push(request);
  });

  const cardsFor = (columnKey) =>
    columnKey === 'in-package' ? inPackageClients : (requestsByColumn[columnKey] || []);

  // Move a request to a new status column (drag-and-drop). Writes the request
  // status and syncs the mirrored `positions` doc so the client portal stays in
  // step — except 'Bid', which is intentionally not a portal-visible status.
  const updateRequestStatus = async (requestId, columnKey) => {
    const newStatus = COLUMN_TO_REQUEST_STATUS[columnKey];
    if (!newStatus) return;
    const request = positionRequests.find((r) => r.id === requestId);
    if (!request || request.status === newStatus) return;

    // Optimistic update so the card jumps columns immediately.
    setPositionRequests((prev) =>
      prev.map((r) => (r.id === requestId ? { ...r, status: newStatus } : r))
    );

    try {
      await updateDoc(doc(db, 'positionRequests', requestId), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });

      const positionsSnap = await getDocs(
        query(collection(db, 'positions'), where('positionRequestId', '==', requestId))
      );
      const positionStatus = COLUMN_TO_POSITION_STATUS[columnKey];
      await Promise.all(
        positionsSnap.docs.map((positionDoc) =>
          updateDoc(doc(db, 'positions', positionDoc.id), {
            status: positionStatus,
            updatedAt: new Date().toISOString(),
          })
        )
      );
    } catch (error) {
      console.error('Error updating request status:', error);
      // Roll back the optimistic change on failure.
      setPositionRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: request.status } : r))
      );
      alert('Could not update status. Please try again.');
    }
  };

  const handleColumnDrop = (e, columnKey) => {
    e.preventDefault();
    setDragOverColumn(null);
    const requestId = e.dataTransfer.getData('text/plain') || draggingRequestId;
    setDraggingRequestId(null);
    if (requestId && REQUEST_COLUMN_KEYS.includes(columnKey)) {
      updateRequestStatus(requestId, columnKey);
    }
  };

  // ---- Shared style tokens ----
  const surface = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800';
  const cardBase = `${surface} rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer`;
  const inputBase = `w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
    darkMode
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
  }`;
  const labelBase = 'block text-xs font-semibold uppercase tracking-wider mb-1.5 text-gray-500 dark:text-gray-400';

  // Card for an "Available" client — opens the create modal scoped to them.
  const renderPackageCard = (account) => {
    const balance = hiringByAccountId[account.id] || {};
    return (
      <div
        key={account.id}
        onClick={() => openCreateForClient(account)}
        className={`group ${cardBase} p-4`}
        title="Start a hiring request for this client"
      >
        <div className="flex flex-col items-center text-center">
          <HiringAvatarCluster
            account={account}
            company={account.company}
            teamMembers={teamByAccountId[account.id] || []}
            showNames={false}
          />
          <h4 className="w-full font-semibold text-sm text-gray-900 dark:text-white break-words [overflow-wrap:anywhere]">
            {account.company}
          </h4>
          {teamNamesFor(account) && (
            <p className="w-full text-xs text-gray-500 dark:text-gray-400 truncate">{teamNamesFor(account)}</p>
          )}
          <span className="mt-2.5 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
            {balance.remaining} hire{balance.remaining === 1 ? '' : 's'} available
          </span>
          <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400 opacity-80 group-hover:opacity-100 transition-opacity">
            Start a request <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    );
  };

  // Card for a position request — draggable between status columns; opens its
  // detail overview on click.
  const renderRequestCard = (request) => {
    const account = resolveRequestAccount(request);
    const names = teamNamesFor(account) ||
      (request.requestedByName && request.requestedByName !== 'Unknown User'
        ? request.requestedByName
        : (request.mainContactName || ''));
    const column = KANBAN_COLUMNS.find((c) => c.key === STATUS_TO_COLUMN[request.status]);
    return (
      <div
        key={request.id}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', request.id);
          e.dataTransfer.effectAllowed = 'move';
          setDraggingRequestId(request.id);
        }}
        onDragEnd={() => {
          setDraggingRequestId(null);
          setDragOverColumn(null);
        }}
        onClick={() => navigate(`/crm/fulfillment/hr/request/${request.id}`)}
        className={`group relative ${cardBase} p-4 ${draggingRequestId === request.id ? 'opacity-50 ring-2 ring-dashed ring-gray-300' : ''}`}
      >
        <GripVertical className="absolute top-3 right-2.5 w-4 h-4 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
        <div className="flex flex-col items-center text-center">
          <HiringAvatarCluster
            account={account}
            company={request.companyName}
            teamMembers={account ? (teamByAccountId[account.id] || []) : []}
            showNames={false}
          />
          <h4 className="w-full font-semibold text-sm text-gray-900 dark:text-white break-words [overflow-wrap:anywhere]">
            {request.companyName}
          </h4>
          {names && (
            <p className="w-full text-xs text-gray-500 dark:text-gray-400 truncate">{names}</p>
          )}
          <p
            className="mt-2 w-full text-sm font-medium break-words [overflow-wrap:anywhere]"
            style={{ color: column?.color }}
          >
            {request.position}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center justify-center gap-1.5">
            {urgencyChip(request.urgency)}
            {request.budget && (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                <DollarSign className="w-3 h-3" />
                {String(request.budget).replace(/^\$/, '')}
              </span>
            )}
            {formatShortDate(request.requestDate) && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                <Calendar className="w-3 h-3" />
                {formatShortDate(request.requestDate)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Table view — shown when a single stage tile is selected (mirrors the
  // Accounts/Leads page switching to a table for a specific stage).
  const renderTable = () => {
    const column = KANBAN_COLUMNS.find((c) => c.key === statusFilter);
    const isPackage = statusFilter === 'in-package';
    const rows = cardsFor(statusFilter);
    const thBase = 'px-5 py-3 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider';
    const tdBase = 'px-5 py-3.5 text-sm text-gray-900 dark:text-white';
    const rowClass = 'hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer transition-colors';

    const companyCell = (account, company) => (
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0 scale-[0.8] origin-left -mr-2">
          <HiringAvatarCluster account={account} company={company} teamMembers={[]} showNames={false} />
        </div>
        <span className="font-semibold truncate">{company}</span>
      </div>
    );

    return (
      <div className={`${surface} rounded-2xl shadow-sm overflow-hidden`}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: column?.color }} />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">{column?.title}</h2>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {rows.length}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setStatusFilter('')}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Back to board
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-gray-800/60">
              <tr>
                <th className={thBase}>Company</th>
                <th className={thBase}>Team</th>
                {isPackage ? (
                  <th className={thBase}>Hires Available</th>
                ) : (
                  <>
                    <th className={thBase}>Position</th>
                    <th className={thBase}>Urgency</th>
                    <th className={thBase}>Budget</th>
                    <th className={thBase}>Date</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {isPackage
                ? rows.map((account) => (
                    <tr key={account.id} onClick={() => openCreateForClient(account)} className={rowClass}>
                      <td className={tdBase}>{companyCell(account, account.company)}</td>
                      <td className={`${tdBase} text-gray-500 dark:text-gray-400`}>{teamNamesFor(account) || '—'}</td>
                      <td className={tdBase}>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                          {(hiringByAccountId[account.id]?.remaining) || 0}
                        </span>
                      </td>
                    </tr>
                  ))
                : rows.map((request) => {
                    const account = resolveRequestAccount(request);
                    const names = teamNamesFor(account) ||
                      (request.requestedByName && request.requestedByName !== 'Unknown User' ? request.requestedByName : request.mainContactName || '');
                    return (
                      <tr
                        key={request.id}
                        onClick={() => navigate(`/crm/fulfillment/hr/request/${request.id}`)}
                        className={rowClass}
                      >
                        <td className={tdBase}>{companyCell(account, request.companyName)}</td>
                        <td className={`${tdBase} text-gray-500 dark:text-gray-400`}>{names || '—'}</td>
                        <td className={`${tdBase} font-medium`}>{request.position}</td>
                        <td className="px-5 py-3.5">{urgencyChip(request.urgency)}</td>
                        <td className={`${tdBase} text-gray-500 dark:text-gray-400`}>
                          {request.budget ? (
                            <span className="inline-flex items-center">
                              <DollarSign className="w-3 h-3 mr-0.5 text-gray-400" />
                              {String(request.budget).replace(/^\$/, '')}
                            </span>
                          ) : '—'}
                        </td>
                        <td className={`${tdBase} text-gray-500 dark:text-gray-400`}>{formatDate(request.requestDate)}</td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="p-14 text-center">
            {column && (
              <div className={`w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center ${column.tile}`}>
                <column.Icon className={`w-7 h-7 ${column.text}`} />
              </div>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400">{column?.empty}</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} />
          <p className={`mt-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading hiring fulfillment…</p>
        </div>
      </div>
    );
  }

  const totalOpen = requestsByColumn.bid.length + requestsByColumn['in-progress'].length;

  return (
    <div className={`min-h-screen ${isMobile ? 'p-3' : 'p-6'}`}>
      <PortalBackground />
      <div className="relative">
        {/* Header */}
        <div className="relative flex items-center justify-between gap-3 mb-6 min-h-[44px]">
          <BackButton to="/crm/fulfillment" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900 dark:text-white whitespace-nowrap`}>
              Hiring Fulfillment
            </h1>
            {!isMobile && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 whitespace-nowrap">
                {totalOpen} open request{totalOpen === 1 ? '' : 's'} · {inPackageClients.length} client{inPackageClients.length === 1 ? '' : 's'} ready to hire
              </p>
            )}
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold shadow-sm hover:bg-red-700 hover:shadow-md transition-all"
            title="Create a position request on behalf of a client"
          >
            <Plus className="w-4 h-4" /> {isMobile ? 'New' : 'New Request'}
          </button>
        </div>

        {/* Stage tiles — click to focus one stage as a table, click again to return to the board */}
        <div className={`grid gap-3 mb-4 ${isMobile ? 'grid-cols-2' : 'grid-cols-4'}`}>
          {KANBAN_COLUMNS.map((column) => {
            const count = column.key === 'in-package' && loadingPackage ? null : cardsFor(column.key).length;
            const selected = statusFilter === column.key;
            return (
              <button
                key={column.key}
                type="button"
                onClick={() => setStatusFilter(selected ? '' : column.key)}
                className={`group rounded-xl border text-left p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  selected
                    ? `border-transparent ring-2 ${column.ring} ${column.tile} shadow-sm`
                    : surface
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${column.tile}`}>
                    <span className="text-2xl" aria-hidden="true">{column.emoji}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-2xl font-bold leading-none ${count === 0 ? 'text-gray-300 dark:text-gray-600' : column.text}`}>
                      {count === null ? '…' : count}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">{column.title}</p>
                  </div>
                  <ArrowRight className={`w-4 h-4 shrink-0 transition-all ${selected ? column.text : 'text-gray-300 dark:text-gray-600 group-hover:text-gray-500'}`} />
                </div>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by company, contact, position, or notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${inputBase} pl-10 pr-10 py-3 rounded-xl shadow-sm`}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Focused stage → table. Otherwise the full board. */}
        {statusFilter ? renderTable() : (
          <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-4'}`}>
            {KANBAN_COLUMNS.map((column) => {
              const isPackage = column.key === 'in-package';
              const isDropTarget = REQUEST_COLUMN_KEYS.includes(column.key);
              const cards = cardsFor(column.key);
              const loadingCol = isPackage && loadingPackage;
              const isDragOver = dragOverColumn === column.key;
              const dragging = !!draggingRequestId;
              return (
                <div
                  key={column.key}
                  onDragOver={isDropTarget ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverColumn !== column.key) setDragOverColumn(column.key); } : undefined}
                  onDragLeave={isDropTarget ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverColumn((c) => (c === column.key ? null : c)); } : undefined}
                  onDrop={isDropTarget ? (e) => handleColumnDrop(e, column.key) : undefined}
                  className={`rounded-2xl border flex flex-col transition-all duration-200 ${
                    isDragOver
                      ? `border-transparent ring-2 ${column.ring} ${column.tile}`
                      : dragging && isDropTarget
                        ? 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/60 dark:bg-gray-900/40'
                        : 'border-gray-200/80 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/40'
                  }`}
                  style={{ minHeight: isMobile ? '260px' : 'calc(100vh - 340px)' }}
                >
                  {/* Column header */}
                  <div className="flex items-center justify-between gap-2 px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: column.color }} />
                      <span className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">{column.title}</span>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                      {loadingCol ? '…' : cards.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div className="flex flex-col gap-3 px-3 pb-3 overflow-y-auto flex-1" style={{ maxHeight: isMobile ? '420px' : 'calc(100vh - 380px)' }}>
                    {loadingCol ? (
                      <div className="flex justify-center items-center py-10">
                        <BBALoader size={32} />
                      </div>
                    ) : cards.length === 0 ? (
                      <div className="flex flex-col items-center justify-center text-center py-12 px-3">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 ${column.tile}`}>
                          <column.Icon className={`w-6 h-6 ${column.text}`} />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{column.empty}</p>
                      </div>
                    ) : (
                      isPackage
                        ? cards.map((account) => renderPackageCard(account))
                        : cards.map((request) => renderRequestCard(request))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Position Request Modal (on behalf of a client) */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${darkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white'} rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}>
            <div className="relative flex items-center justify-center px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">New Position Request</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Created on behalf of a client</p>
              </div>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Client */}
              <div>
                <label className={labelBase}>
                  Client <span className="text-red-500">*</span>
                </label>
                <select
                  value={createForm.accountId}
                  onChange={(e) => setCreateForm({ ...createForm, accountId: e.target.value })}
                  disabled={clientsLoading}
                  className={inputBase}
                >
                  <option value="">{clientsLoading ? 'Loading clients…' : 'Select a client…'}</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.company}
                    </option>
                  ))}
                </select>
                <p className="text-xs mt-1.5 text-gray-500 dark:text-gray-400">
                  If the client has a portal login, this request will also appear in their hiring hub.
                </p>
              </div>

              {/* Position title */}
              <div>
                <label className={labelBase}>
                  Position Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={createForm.position}
                  onChange={(e) => setCreateForm({ ...createForm, position: e.target.value })}
                  placeholder="e.g., Project Manager"
                  className={inputBase}
                />
              </div>

              {/* Requirements */}
              <div>
                <label className={labelBase}>Requirements & Notes</label>
                <textarea
                  rows={4}
                  value={createForm.requirements}
                  onChange={(e) => setCreateForm({ ...createForm, requirements: e.target.value })}
                  placeholder="Role requirements, experience needed, or notes…"
                  className={inputBase}
                />
              </div>

              {/* Urgency + Budget */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelBase}>Urgency</label>
                  <select
                    value={createForm.urgency}
                    onChange={(e) => setCreateForm({ ...createForm, urgency: e.target.value })}
                    className={inputBase}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div>
                  <label className={labelBase}>Budget</label>
                  <input
                    type="text"
                    value={createForm.budget}
                    onChange={(e) => setCreateForm({ ...createForm, budget: e.target.value })}
                    placeholder="e.g., $80,000 - $120,000"
                    className={inputBase}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold ${
                  darkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRequest}
                disabled={creating || !createForm.accountId || !createForm.position.trim()}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors ${
                  creating || !createForm.accountId || !createForm.position.trim()
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                <Plus className="w-4 h-4" /> {creating ? 'Creating…' : 'Create Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRFulfillment;
